import type { Session, Ticket, TicketOpsStatus } from "./types";

export type SettleStatus =
  | "clear"
  | "await_topup"
  | "await_refund"
  | "topup_paid"
  | "refunded"
  | "none";

export type PricePackage = "afternoon" | "evening" | "both";

export const ONSITE_DOOR_PRICES: Record<PricePackage, number> = {
  afternoon: 900,
  evening: 900,
  both: 1600,
};

export interface OnsiteTicketPlan {
  opsStatus: Exclude<TicketOpsStatus, "" | "refund">;
  ticketType: string;
  sessions: Session[];
  listPrice: number;
  settleDelta: number;
  settleStatus: "await_topup";
}

const ONSITE_PLAN_BY_STATUS: Record<OnsiteTicketPlan["opsStatus"], {
  package: PricePackage;
  ticketType: string;
  sessions: Session[];
}> = {
  onsite_afternoon: { package: "afternoon", ticketType: "現場午場", sessions: ["afternoon"] },
  onsite_evening: { package: "evening", ticketType: "現場晚場", sessions: ["evening"] },
  onsite_both: { package: "both", ticketType: "現場通票", sessions: ["afternoon", "evening"] },
};

export interface PriceTables {
  presale: Record<PricePackage, number>;
  onsite: Record<PricePackage, number>;
}

export interface SettleComputation {
  priorCredit: number;
  listPrice: number;
  settleDelta: number;
  settleStatus: SettleStatus;
  couponEligible: boolean;
  package: PricePackage;
  creditNeedsReview: boolean;
}

export function phoneDigits(phone: string): string {
  return String(phone || "").replace(/\D/g, "");
}

export function phoneLastN(phone: string, n: number): string {
  const digits = phoneDigits(phone);
  return digits.slice(-n);
}

/** Normalize name+email for identity join (trim + lower-case). Empty if either missing. */
export function normalizeNameEmailKey(name: string, email: string): string {
  const n = String(name || "").trim().toLowerCase();
  const e = String(email || "").trim().toLowerCase();
  if (!n || !e) return "";
  return `${n}|${e}`;
}

/**
 * Index items by unique name+email. Ambiguous keys (2+ people) are omitted from `unique`
 * and listed in `ambiguous` so callers can flag needsReview instead of merging.
 */
export function indexUniqueByNameEmail<T>(
  items: T[],
  getName: (item: T) => string,
  getEmail: (item: T) => string,
): { unique: Map<string, T>; ambiguous: { key: string; count: number; items: T[] }[] } {
  const buckets = new Map<string, T[]>();
  for (const item of items) {
    const key = normalizeNameEmailKey(getName(item), getEmail(item));
    if (!key) continue;
    const list = buckets.get(key) || [];
    list.push(item);
    buckets.set(key, list);
  }
  const unique = new Map<string, T>();
  const ambiguous: { key: string; count: number; items: T[] }[] = [];
  for (const [key, list] of buckets) {
    if (list.length === 1) unique.set(key, list[0]);
    else ambiguous.push({ key, count: list.length, items: list });
  }
  return { unique, ambiguous };
}

export type CreditMatchMethod = "phone" | "name_email";

/**
 * Match a keep-credit to an 8/16 ticket.
 * Primary: phone. Fallback: unique name+email on both sides (phone may differ).
 */
export function matchKeepCredit<TCredit extends { id: string }>(
  ticket: { phone?: string; name?: string; email?: string },
  creditsByPhone: Map<string, TCredit[]>,
  creditsByNameEmail: Map<string, TCredit>,
  ticketsByNameEmail: Map<string, unknown>,
  usedIds: Set<string>,
): { credit: TCredit | null; matchMethod: CreditMatchMethod | null } {
  const phone = phoneDigits(ticket.phone || "");
  if (phone) {
    const pool = creditsByPhone.get(phone) || [];
    const hit = pool.find((c) => !usedIds.has(c.id));
    if (hit) return { credit: hit, matchMethod: "phone" };
  }

  const key = normalizeNameEmailKey(ticket.name || "", ticket.email || "");
  if (!key) return { credit: null, matchMethod: null };
  if (!ticketsByNameEmail.has(key) || !creditsByNameEmail.has(key)) {
    return { credit: null, matchMethod: null };
  }
  const credit = creditsByNameEmail.get(key);
  if (!credit || usedIds.has(credit.id)) return { credit: null, matchMethod: null };
  return { credit, matchMethod: "name_email" };
}

export function packageFromTicketType(ticketType: string): PricePackage {
  if (ticketType.includes("通票")) return "both";
  if (ticketType.includes("晚場")) return "evening";
  return "afternoon";
}

/** Parse 「…：800」 / 「800」 style face value from ticket type label. */
export function parsePriceFromTicketType(ticketType: string): number | null {
  const match = String(ticketType || "").match(/[：:]\s*(\d{3,5})\b/) || String(ticketType || "").match(/\b(\d{3,5})\b/);
  if (!match) return null;
  return Number(match[1]);
}

export function isCounterPaymentPending(ticket: Pick<Ticket, "paymentMethod" | "paymentRecorded">): boolean {
  return !ticket.paymentRecorded && String(ticket.paymentMethod || "").includes("杰克音樂櫃台付款");
}

/**
 * Convert a named form ticket to an on-site package.
 * - Unpaid Jack's counter reservations choose any on-site package and owe its full price.
 * - A paid single-session presale may only expand to the pass and credits its paid face value.
 */
export function onsiteTicketPlan(
  ticket: Ticket,
  opsStatus: string,
): OnsiteTicketPlan | null {
  if (!(opsStatus in ONSITE_PLAN_BY_STATUS)) return null;
  if (ticket.source !== "form" || ticket.kind !== "presale") return null;
  if (ticket.opsStatus) return null;
  if (Number(ticket.priorCredit) > 0) return null;
  if (["await_topup", "await_refund", "refunded"].includes(ticket.settleStatus || "")) return null;

  const status = opsStatus as OnsiteTicketPlan["opsStatus"];
  const target = ONSITE_PLAN_BY_STATUS[status];
  if ((ticket.units || []).some((unit) => unit.checkedIn && !target.sessions.includes(unit.session))) {
    return null;
  }
  const quantity = Math.max(1, Number(ticket.quantity) || 1);
  const listPrice = ONSITE_DOOR_PRICES[target.package] * quantity;

  if (isCounterPaymentPending(ticket)) {
    return {
      opsStatus: status,
      ticketType: target.ticketType,
      sessions: [...target.sessions],
      listPrice,
      settleDelta: listPrice,
      settleStatus: "await_topup",
    };
  }

  if (!ticket.paymentRecorded || status !== "onsite_both") return null;

  const originalType = ticket.originalTicketType || ticket.ticketType;
  const originalPackage = packageFromTicketType(originalType);
  if (originalPackage === "both") return null;
  const originalUnitPrice = parsePriceFromTicketType(originalType);
  if (originalUnitPrice == null) return null;

  const settleDelta = listPrice - originalUnitPrice * quantity;
  if (settleDelta <= 0) return null;
  return {
    opsStatus: status,
    ticketType: target.ticketType,
    sessions: [...target.sessions],
    listPrice,
    settleDelta,
    settleStatus: "await_topup",
  };
}

export function listPriceFor(
  pkg: PricePackage,
  prices: PriceTables,
  useOnsitePricing: boolean,
): number {
  const table = useOnsitePricing ? prices.onsite : prices.presale;
  return table[pkg];
}

export function computeSettleDelta(listPrice: number, priorCredit: number): number {
  return Math.round(listPrice) - Math.round(priorCredit);
}

export function settleStatusFromDelta(
  delta: number,
  options: { topupPaid?: boolean; refunded?: boolean } = {},
): SettleStatus {
  if (options.refunded) return "refunded";
  if (delta === 0) return "clear";
  if (delta > 0) return options.topupPaid ? "topup_paid" : "await_topup";
  return "await_refund";
}

export function computeSettle(input: {
  ticketType: string;
  priorCredit: number;
  couponEligible: boolean;
  useOnsitePricing: boolean;
  prices: PriceTables;
  topupPaid?: boolean;
  refunded?: boolean;
  creditNeedsReview?: boolean;
}): SettleComputation {
  const pkg = packageFromTicketType(input.ticketType);
  const fromLabel = parsePriceFromTicketType(input.ticketType);
  const listPrice =
    fromLabel != null && !input.useOnsitePricing
      ? fromLabel
      : listPriceFor(pkg, input.prices, input.useOnsitePricing);
  const settleDelta = computeSettleDelta(listPrice, input.priorCredit);
  return {
    priorCredit: Math.round(input.priorCredit),
    listPrice,
    settleDelta,
    settleStatus: settleStatusFromDelta(settleDelta, {
      topupPaid: input.topupPaid,
      refunded: input.refunded,
    }),
    couponEligible: Boolean(input.couponEligible),
    package: pkg,
    creditNeedsReview: Boolean(input.creditNeedsReview),
  };
}

export function registryIsKeep(disposition: string, keepKeywords: string[], refundKeywords: string[]): boolean {
  const text = String(disposition || "");
  if (refundKeywords.some((k) => text.includes(k))) return false;
  return keepKeywords.some((k) => text.includes(k));
}

export type CreditUseIntent = "use" | "do_not_use" | "unspecified";

/** Whether the 8/16 form explicitly consumes the 7/11 credit this event. */
export function creditUseIntent(paymentMethod: string | undefined): CreditUseIntent {
  const text = String(paymentMethod || "");
  if (/不延用|保留.*權益/.test(text)) return "do_not_use";
  if (/延用\s*7\/11|7\/11.*延用|延用.*票款/.test(text)) return "use";
  return "unspecified";
}

/** Extract last-5 style suffix candidates from bank 備註 / 對方帳號. */
export function bankSuffixCandidates(note: string, counterAccount: string): string[] {
  const found = new Set<string>();
  const accountDigits = phoneDigits(counterAccount);
  if (accountDigits.length >= 5) found.add(accountDigits.slice(-5));

  const noteText = String(note || "");
  for (const match of noteText.matchAll(/(\d{5,})/g)) {
    const digits = match[1];
    found.add(digits.slice(-5));
  }
  // Patterns like 017A003XXXX82143 → trailing visible digits before spaces
  for (const match of noteText.matchAll(/([0-9A-Za-z]{4,})XXXX(\d{4,})/gi)) {
    found.add(match[2].slice(-5).padStart(5, "0").slice(-5));
  }
  return [...found].filter((s) => s.length === 5);
}

/**
 * Full-price bank verification target.
 * A form amount or uploaded proof may already set paymentRecorded=true, but that
 * must not suppress independent bank-statement reconciliation.
 */
export function isFullPaymentBankCandidate(ticket: {
  priorCredit?: number;
  settleStatus?: SettleStatus | "none" | null;
  transferSuffix?: string;
  paymentMethod?: string;
  paymentAmount?: string;
  paymentRecorded?: boolean;
}): boolean {
  const claimedAmount = Number(String(ticket.paymentAmount || "").replace(/,/g, "")) || 0;
  return Boolean(
    (!ticket.priorCredit || ticket.settleStatus === "none") &&
      ticket.transferSuffix &&
      (/匯款/.test(ticket.paymentMethod || "") || claimedAmount > 0),
  );
}

export interface BankDeposit {
  date: string;
  amount: number;
  note: string;
  counterAccount: string;
  suffixes: string[];
  rowIndex: number;
}

export interface RemittanceTarget {
  id: string;
  transferSuffix: string;
  amount: number;
  /** Already matched bank row index, if any */
  matchedRowIndex?: number;
}

export interface BankMatchResult {
  matched: { targetId: string; rowIndex: number; amount: number; confidence: "high" }[];
  ambiguous: { targetId: string; rowIndexes: number[]; amount: number }[];
  unmatchedTargets: string[];
  unmatchedDeposits: number[];
}

/**
 * High-confidence: unique (suffix + amount) pair with exactly one unused deposit.
 */
export function matchBankDeposits(
  targets: RemittanceTarget[],
  deposits: BankDeposit[],
): BankMatchResult {
  const usedRows = new Set<number>();
  const matched: BankMatchResult["matched"] = [];
  const ambiguous: BankMatchResult["ambiguous"] = [];
  const unmatchedTargets: string[] = [];

  const pending = targets.filter((t) => t.amount > 0 && phoneDigits(t.transferSuffix).length >= 4);

  for (const target of pending) {
    const suffix = phoneDigits(target.transferSuffix).slice(-5);
    const amount = Math.round(target.amount);
    const candidates = deposits.filter((dep) => {
      if (usedRows.has(dep.rowIndex)) return false;
      if (Math.round(dep.amount) !== amount) return false;
      return dep.suffixes.some((s) => s === suffix || s.endsWith(suffix) || suffix.endsWith(s));
    });

    if (candidates.length === 1) {
      usedRows.add(candidates[0].rowIndex);
      matched.push({
        targetId: target.id,
        rowIndex: candidates[0].rowIndex,
        amount,
        confidence: "high",
      });
    } else if (candidates.length > 1) {
      ambiguous.push({
        targetId: target.id,
        rowIndexes: candidates.map((c) => c.rowIndex),
        amount,
      });
    } else {
      unmatchedTargets.push(target.id);
    }
  }

  const unmatchedDeposits = deposits
    .filter((dep) => dep.amount > 0 && !usedRows.has(dep.rowIndex))
    .map((dep) => dep.rowIndex);

  return { matched, ambiguous, unmatchedTargets, unmatchedDeposits };
}

export function parseBankCsv(text: string): BankDeposit[] {
  const lines = text.replace(/^\uFEFF/, "").split(/\r?\n/).filter((line) => line.trim());
  if (lines.length < 2) return [];
  const header = splitCsvLine(lines[0]).map((h) => h.trim());
  const idx = (name: string) => header.indexOf(name);

  const iDate = idx("交易日期");
  const iIn = idx("存入金額");
  const iNote = idx("備註");
  let iAccount = idx("對方帳號");
  const hasTrailingCounterpartyColumns = lines
    .slice(1)
    .some((line) => splitCsvLine(line).length >= 15);
  if (
    iAccount < 0 &&
    header.length === 13 &&
    header[11] === "備註" &&
    hasTrailingCounterpartyColumns
  ) {
    iAccount = 14;
  }
  if (iDate < 0 || iIn < 0) return [];

  const deposits: BankDeposit[] = [];
  for (let rowIndex = 1; rowIndex < lines.length; rowIndex += 1) {
    const cols = splitCsvLine(lines[rowIndex]);
    const amount = Number(String(cols[iIn] || "0").replace(/,/g, ""));
    if (!(amount > 0)) continue;
    const note = iNote >= 0 ? unquote(cols[iNote] || "") : "";
    const counterAccount = iAccount >= 0 ? unquote(cols[iAccount] || "") : "";
    deposits.push({
      date: unquote(cols[iDate] || ""),
      amount,
      note,
      counterAccount,
      suffixes: bankSuffixCandidates(note, counterAccount),
      rowIndex,
    });
  }
  return deposits;
}

function unquote(value: string): string {
  const text = value.trim();
  if (text.startsWith("'") && text.endsWith("'")) return text.slice(1, -1).trim();
  return text;
}

function splitCsvLine(line: string): string[] {
  const out: string[] = [];
  let cur = "";
  let inQuotes = false;
  for (let i = 0; i < line.length; i += 1) {
    const ch = line[i];
    if (ch === '"') {
      if (inQuotes && line[i + 1] === '"') {
        cur += '"';
        i += 1;
      } else {
        inQuotes = !inQuotes;
      }
      continue;
    }
    if (ch === "," && !inQuotes) {
      out.push(cur);
      cur = "";
      continue;
    }
    cur += ch;
  }
  out.push(cur);
  return out;
}

export function ticketAwaitingTopup(ticket: Ticket): boolean {
  return ticket.settleStatus === "await_topup";
}

export function ticketAwaitingRefund(ticket: Ticket): boolean {
  return ticket.settleStatus === "await_refund";
}

export function settlePaymentNeedsAttention(ticket: Ticket): boolean {
  if (ticket.settleStatus === "await_topup") return true;
  if (ticket.settleStatus === "await_refund") return true;
  return false;
}
